import { Component, type ErrorInfo, type ReactNode } from 'react'
import { Translation } from 'react-i18next'
import { Sentry } from '../lib/sentry'

interface Props {
  children: ReactNode
  fallback?: ReactNode
}

interface State {
  hasError: boolean
  error: Error | null
  isChunkError: boolean
}

/** Detect dynamic-import / lazy-chunk failures (e.g. after a new deploy). */
function isChunkLoadError(err: Error | null): boolean {
  if (!err) return false
  const msg = err.message ?? ''
  return (
    msg.includes('Failed to fetch dynamically imported module') ||
    msg.includes('Importing a module script failed') ||
    msg.includes('Loading chunk') ||
    err.name === 'ChunkLoadError'
  )
}

export class ErrorBoundary extends Component<Props, State> {
  constructor(props: Props) {
    super(props)
    this.state = { hasError: false, error: null, isChunkError: false }
  }

  static getDerivedStateFromError(error: Error): State {
    return { hasError: true, error, isChunkError: isChunkLoadError(error) }
  }

  componentDidCatch(error: Error, info: ErrorInfo) {
    if (!isChunkLoadError(error)) {
      console.error('[ErrorBoundary] Uncaught error:', error, info)
      // Report to Sentry — gated inside lib/sentry.ts via VITE_SENTRY_DSN
      // (initSentry() is a no-op without DSN, so captureException is also
      // a no-op in dev/demo without secrets).
      if ((import.meta as { env?: { VITE_SENTRY_DSN?: string } }).env?.VITE_SENTRY_DSN) {
        Sentry.captureException(error, {
          contexts: { react: { componentStack: info.componentStack ?? '' } },
        })
      }
    }
  }

  render() {
    if (this.state.hasError) {
      if (this.props.fallback) return this.props.fallback

      // Chunk-load failures mean a new deploy happened while the user was on the page.
      // The old chunk URLs are gone — a full page reload is the only reliable fix.
      if (this.state.isChunkError) {
        return (
          <Translation>
            {(t) => (
              <div className="flex flex-col items-center justify-center min-h-screen p-8 text-center">
                <h2 className="text-xl font-semibold text-coral mb-2">{t('errors.newVersionAvailable', 'Neue Version verfügbar')}</h2>
                <p className="text-zinc-500 mb-4 text-sm">
                  {t('errors.reloadRequired', 'Die Anwendung wurde aktualisiert. Bitte Seite neu laden.')}
                </p>
                <button
                  className="px-4 py-2 bg-accent text-white rounded hover:bg-accent-hover text-sm"
                  onClick={() => window.location.reload()}
                >
                  {t('errors.reload', 'Seite neu laden')}
                </button>
              </div>
            )}
          </Translation>
        )
      }

      return (
        <Translation>
          {(t) => (
            <div className="flex flex-col items-center justify-center min-h-screen p-8 text-center">
              <h2 className="text-xl font-semibold text-coral mb-2">{t('errors.errorOccurred')}</h2>
              <p className="text-zinc-500 mb-4 text-sm">
                {this.state.error?.message ?? t('errors.unknownError')}
              </p>
              <button
                className="px-4 py-2 bg-accent text-white rounded hover:bg-accent-hover text-sm"
                onClick={() => this.setState({ hasError: false, error: null, isChunkError: false })}
              >
                {t('errors.tryAgain')}
              </button>
            </div>
          )}
        </Translation>
      )
    }

    return this.props.children
  }
}
